/**
 * PURPOSE: Tarjeta de posición enfocada en camiseta grande para árbitros.
 * RESPONSIBILITIES:
 * - Mostrar la camiseta como elemento principal del card.
 * - Mantener posición romana, capitán, líbero, vínculo y saque como señales absolutas.
 * - No mostrar nombre para conservar lectura inmediata en celular.
 * ROUTE: src/pages/VolleyballViewer/components/ViewerPositionCard.jsx
 */

import { FaVolleyball } from "react-icons/fa6";
import { romanPosition } from "../utils/volleyballViewerCourt.js";

function cardTone(player, serving) {
  if (player?.isCaptain && player?.isLibero) {
    return serving
      ? "border-yellow-500 bg-gradient-to-br from-yellow-300 via-amber-200 to-emerald-100 ring-2 ring-emerald-300"
      : "border-yellow-500 bg-gradient-to-br from-yellow-300 via-amber-200 to-indigo-100 ring-1 ring-yellow-400/70";
  }
  if (player?.isCaptain) {
    return serving
      ? "border-yellow-500 bg-gradient-to-br from-yellow-300 via-amber-200 to-emerald-100 ring-2 ring-emerald-300"
      : "border-yellow-500 bg-gradient-to-br from-yellow-300 via-amber-200 to-yellow-100 ring-1 ring-yellow-400/70";
  }
  if (player?.isLibero) {
    return serving
      ? "border-emerald-400 bg-gradient-to-br from-indigo-100 to-emerald-100 ring-2 ring-emerald-300"
      : "border-indigo-300 bg-indigo-100";
  }
  return serving
    ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-300"
    : "border-orange-200 bg-orange-100";
}

function relationMeta(relationBadge) {
  if (!relationBadge) return null;

  const jersey = String(relationBadge?.jersey || "").trim();
  if (!jersey) return null;

  const type = String(relationBadge?.type || "").toUpperCase();

  return {
    prefix: type === "LIBERO" ? "L" : "↔",
    jersey,
    title: type === "LIBERO" ? `Líbero vinculado con #${jersey}` : `Cambio vinculado con #${jersey}`,
  };
}

export default function ViewerPositionCard({ player, serving }) {
  const position = Number(player?.position);
  const label = player?.positionLabel || player?.roman || romanPosition(position);
  const jersey = player?.jersey || "-";
  const relation = relationMeta(player?.relationBadge);

  return (
    <div
      className={[
        "relative flex min-h-0 min-w-0 overflow-hidden rounded-xl border shadow-sm [container-type:size]",
        "items-center justify-center p-0.5 text-center",
        cardTone(player, serving),
      ].join(" ")}
      title={player?.displayName ? `${label} · #${jersey} · ${player.displayName}` : `${label} · #${jersey}`}
    >
      <div className="pointer-events-none absolute left-1 top-1 rounded-md bg-slate-950/85 px-1.5 py-0.5 text-[8px] font-black leading-none text-white shadow-sm">
        {label}
      </div>

      <div className="pointer-events-none absolute right-1 top-1 flex gap-0.5">
        {player?.isCaptain ? (
          <span className="rounded-md bg-yellow-400 px-1 py-0.5 text-[8px] font-black leading-none text-yellow-950 shadow-sm ring-1 ring-yellow-200">C</span>
        ) : null}
        {player?.isLibero ? (
          <span className="rounded-md bg-indigo-600/90 px-1 py-0.5 text-[8px] font-black leading-none text-white shadow-sm">L</span>
        ) : null}
      </div>

      {relation ? (
        <div
          className={[
            "pointer-events-none absolute bottom-1 left-1 z-10",
            "flex max-w-[58%] items-center gap-0.5 overflow-hidden rounded-lg",
            "bg-slate-950/90 px-1 py-0.5 text-white shadow-sm ring-1 ring-white/60",
          ].join(" ")}
          title={relation.title}
        >
          <span className="shrink min-w-0 truncate text-[7px] font-black leading-none opacity-80">
            {relation.prefix}
          </span>
          <span className="flex-none text-[9px] font-black leading-none tabular-nums">
            #{relation.jersey}
          </span>
        </div>
      ) : null}

      {serving ? (
        <div className="pointer-events-none absolute bottom-0.5 right-0.5 z-10 grid h-9 w-9 place-items-center rounded-full bg-white/85 text-emerald-700 shadow-lg ring-1 ring-emerald-200">
          <FaVolleyball className="h-7 w-7" />
        </div>
      ) : null}

      {/* Camiseta proporcional a la tarjeta (unidades cq); sin soporte de container queries queda el tamaño anterior. */}
      <div
        className="select-none whitespace-nowrap font-black leading-none tracking-tight text-slate-950 tabular-nums text-[clamp(1.7rem,8.5vw,3.8rem)] supports-[height:1cqh]:text-[length:min(64cqh,calc(130cqw/var(--jersey-len)))]"
        style={{ "--jersey-len": String(Math.max(String(jersey).length, 2)) }}
      >
        {jersey}
      </div>
    </div>
  );
}
